/**
 * Integration Commands - General integration connection and management
 * 
 * These commands handle connecting various integrations (Plaid, Gmail, etc.)
 * in a unified way.
 */

import { SelfContainedCommand, CommandExecutionContext } from '../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../types/standardToolResults';
import { createFileLogger } from '../../utils/logger';
import { API_BASE_URL } from '../../api/config';

const log = createFileLogger('INTEGRATION_COMMANDS');

/**
 * Setup Integration Tool - Universal Integration Setup
 * 
 * Shows a setup button in chat for missing integrations.
 * Handles both API connections (Stripe, Mixpanel) and OAuth integrations (Plaid, Gmail).
 * 
 * The tool shows a button that:
 * - For API connections: Opens modal for entering API keys
 * - For OAuth integrations: Triggers OAuth popup flow
 */
export const setupIntegrationCommand: SelfContainedCommand = {
  id: 'setup_integration',
  description: `SETUP INTEGRATION: Prompt the user to connect an external service.

Shows a connect button in the chat. The user can connect now, skip for now, or dismiss the suggestion permanently. After connecting, the service's tools become available in your tool list.

**WHEN TO RECOMMEND**
Suggest connecting a service when it would genuinely help with what the user is trying to do. Good signals:
- The user explicitly asks to use a service ("check my GitHub issues", "send a Slack message", "pull my Google Calendar")
- The task clearly requires live data from an external service and you cannot complete it otherwise
- The user is building something that will need a specific integration (e.g. an app that sends emails)

Do NOT suggest a service just because it is loosely related to the topic. One suggestion per service per session — if the user skips it, do not ask again.

**HOW IT WORKS**
1. You call setup_integration with the service name and a brief reason
2. A connect button appears in the chat — the agentic flow pauses here
3. The user clicks Connect (OAuth popup opens), clicks "Maybe later" (skip), or clicks "Don't suggest again"
4. If connected: the service's tools appear in your tool list and you can proceed
5. If skipped or dismissed: continue without it or explain what the user would need to do manually

**INTEGRATION TYPES**
- "oauth" — for all services in the Composio catalog (GitHub, Gmail, Slack, Notion, etc.). Zero-config OAuth, no credentials needed from the user.
- "api" — for custom API-key connections (Stripe, Mixpanel, Apollo, etc.) that require the user to enter an API key.

When in doubt, use "oauth". Only use "api" if you know the service requires an API key.

**EXAMPLES**

User asks to check their GitHub issues:
\`\`\`json
{
  "integrationType": "oauth",
  "serviceName": "GitHub",
  "purpose": "To read and manage your GitHub issues and pull requests"
}
\`\`\`

User wants to send a Slack message:
\`\`\`json
{
  "integrationType": "oauth",
  "serviceName": "Slack",
  "purpose": "To send messages to your Slack workspace"
}
\`\`\`

User asks to create a Stripe payment link:
\`\`\`json
{
  "integrationType": "api",
  "serviceName": "Stripe",
  "purpose": "To create payment links and manage products in Stripe"
}
\`\`\`

User wants to schedule a meeting via Google Calendar:
\`\`\`json
{
  "integrationType": "oauth",
  "serviceName": "Google Calendar",
  "purpose": "To read your calendar and create new events"
}
\`\`\`

**RULES**
- Call this tool ONCE per service per session. Do not re-suggest after a skip or dismissal.
- Do NOT use entity_create to create integrations manually.
- Do NOT call the service's tools before the connection is confirmed.`,

  autonomyLevel: 'ask',  // ✅ PAUSE FLOW: Wait for user to complete setup
  visualTreatment: 'demonstrative',  // Show prominently as it requires user action
  
  schema: {
    parameters: [
      {
        name: 'integrationType',
        type: 'string',
        description: 'Type of integration: "api" (API key-based) or "oauth" (OAuth-based)',
        required: true,
        validator: (value: string) => {
          if (!['api', 'oauth'].includes(value.toLowerCase())) {
            return 'Integration type must be "api" or "oauth"';
          }
          return null;
        }
      },
      {
        name: 'serviceName',
        type: 'string',
        description: 'Name of the service (e.g., "Stripe", "Plaid", "Gmail", "Mixpanel")',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) {
            return 'Service name is required';
          }
          return null;
        }
      },
      {
        name: 'purpose',
        type: 'string',
        description: 'Brief explanation of why this integration is needed for the current task',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) {
            return 'Purpose is required';
          }
          return null;
        }
      },
      {
        name: 'contextEntity',
        type: 'string',
        description: 'Entity ID to automatically link integration to (e.g., current app_project or website_project ID). Creates entity_link automatically after connection.',
        required: false
      },
      {
        name: 'autoLinkRelation',
        type: 'string',
        description: 'Relationship type for auto-linking (default: "uses_api_connection"). Only used if contextEntity is provided.',
        required: false
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: {
          integrationType: 'api',
          serviceName: 'Stripe',
          purpose: 'To create products and prices in Stripe'
        },
        expectedOutput: createSuccessResult('Stripe integration needs to be set up', { needsSetup: true }),
        description: 'Prompt user to set up Stripe API connection'
      }
    ],
    bad: []
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { integrationType, serviceName, purpose, contextEntity, autoLinkRelation } = params;
    
    log.log('[setup_integration] Called with:', { integrationType, serviceName, purpose, contextEntity });
    
    // This tool doesn't actually do anything - the renderer handles showing the setup button
    // We just return success with metadata that the renderer will use
    // The renderer will handle auto-linking if contextEntity is provided
    return createSuccessResult(
      `${serviceName} integration needs to be set up`,
      {
        integrationType,
        serviceName,
        purpose,
        needsSetup: true,
        setupReady: true,
        contextEntity,
        autoLinkRelation: autoLinkRelation || 'uses_api_connection'
      },
      {
        format: 'json'
      }
    );
  }
};


/**
 * Connect Integration Command - Internal OAuth Handler
 * 
 * ⚠️ INTERNAL USE ONLY - Not exposed to AI
 * 
 * This tool performs the actual OAuth connection flow.
 * It's called internally by the setup_integration button for OAuth integrations.
 * AI should always use setup_integration instead.
 */
export const connectIntegrationCommand: SelfContainedCommand = {
  id: 'connect_integration',
  description: `⚠️ INTERNAL TOOL - Use setup_integration instead

Connect an external OAuth integration to River.

This tool is called internally by the setup_integration button.
AI should not call this directly - use setup_integration to show the setup button.

**PURPOSE:**
Performs OAuth flow for integrations (banks, email, calendar, etc.).

**SUPPORTED INTEGRATIONS:**
- **plaid** - Bank account connection (Plaid Banking)
- **gmail** - Email access (Gmail)
- **google_calendar** - Calendar events (Google Calendar)
- **google_drive** - File storage (Google Drive)
- **dropbox** - File storage (Dropbox)

**EXAMPLES:**

Connect Plaid (banking):
{
  "integration": "plaid",
  "autoSync": true
}

Connect Gmail:
{
  "integration": "gmail"
}

**HOW IT WORKS:**
1. Opens OAuth popup for the service
2. User authorizes access
3. Stores encrypted credentials
4. Optionally syncs initial data
5. Makes data available via entities

**WHAT HAPPENS NEXT:**
After connection, data becomes available:
- Bank transactions → \`BankTransaction\` entities
- Emails → \`Email.Gmail\` entities (optional)
- Calendar events → \`CalendarEvent\` entities
- Files → \`File\` entities

Apps can then query this data using \`river.entity.query()\`.`,
  
  autonomyLevel: 'ask', // Always ask user before OAuth
  visualTreatment: 'narrative',
  
  schema: {
    parameters: [
      {
        name: 'integration',
        type: 'string',
        description: 'Integration type: plaid | gmail | google_calendar | google_drive | dropbox',
        required: true,
        examples: ['plaid', 'gmail', 'google_calendar'],
        validator: (value: string) => {
          const valid = ['plaid', 'gmail', 'google_calendar', 'google_drive', 'dropbox'];
          if (!valid.includes(value.toLowerCase())) {
            return `Invalid integration. Must be one of: ${valid.join(', ')}`;
          }
          return null;
        }
      },
      {
        name: 'autoSync',
        type: 'boolean',
        description: 'Whether to automatically sync data after connection (default: true)',
        required: false,
        defaultValue: true
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { integration: 'plaid', autoSync: true },
        expectedOutput: createSuccessResult('Plaid connected', {
          integration: 'plaid',
          status: 'connected',
          dataAvailable: true
        }),
        description: 'Connect Plaid banking',
        context: 'User wants to access bank transaction data'
      },
      {
        input: { integration: 'gmail' },
        expectedOutput: createSuccessResult('Gmail connected', {
          integration: 'gmail',
          status: 'connected'
        }),
        description: 'Connect Gmail',
        context: 'User wants to access email data'
      }
    ],
    bad: [
      {
        input: { integration: 'invalid' },
        error: 'Invalid integration',
        why: 'Integration type not supported',
        fix: 'Use a supported integration type'
      }
    ]
  },
  
  performance: {
    expectedDuration: 30000, // OAuth can take up to 30 seconds
    complexity: 'high'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { integration, autoSync = true } = params;
    
    const integrationType = integration.toLowerCase();
    
    try {
      log.info('[connect_integration] Starting connection:', { integrationType, autoSync });
      
      // Route to specific integration handler
      switch (integrationType) {
        case 'plaid':
          return await connectPlaid(context, autoSync);
        
        case 'gmail':
          return await connectGmail(context);
        
        case 'google_calendar':
          return await connectGoogleCalendar(context);
        
        case 'google_drive':
          return await connectGoogleDrive(context);
        
        case 'dropbox':
          return await connectDropbox(context);
        
        default:
          return createErrorResult(
            `Integration not yet implemented: ${integration}`,
            `The ${integration} integration is planned but not yet available.`,
            'validation'
          );
      }
      
    } catch (error: any) {
      log.error('[connect_integration] Error:', error);
      return createErrorResult(
        'Integration connection failed',
        error.message,
        'execution',
        { metadata: { executionTime: Date.now() - startTime } }
      );
    }
  }
};

/**
 * Plaid Banking Connection
 * 
 * Note: This function returns a Promise that resolves when user completes OAuth.
 * ToolExecutor handles the timeout (5 minutes) - this gives users time to:
 * 1. Read the Plaid popup
 * 2. Search for their bank
 * 3. Enter credentials
 * 4. Wait for authorization
 * 5. Return to River
 */
async function connectPlaid(context: CommandExecutionContext, autoSync: boolean): Promise<StandardToolResult> {
  log.info('[connectPlaid] Starting Plaid connection (5 minute timeout handled by ToolExecutor)');
  
  try {
    // Step 1: Create link token
    const authToken = localStorage.getItem('authToken');
    const linkTokenUrl = `${API_BASE_URL}/plaid/create_link_token`;
    
    log.info('[connectPlaid] Creating link token:', { url: linkTokenUrl });
    
    const linkTokenResponse = await fetch(linkTokenUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      },
        body: JSON.stringify({
        client_name: 'River',
        language: 'en',
        country_codes: ['US'],
        products: ['transactions', 'investments', 'liabilities'],
        redirect_uri: `${window.location.origin}/plaid-oauth`,
      })
    });
    
    if (!linkTokenResponse.ok) {
      const errorText = await linkTokenResponse.text();
      log.error('[connectPlaid] Link token request failed:', { 
        status: linkTokenResponse.status, 
        statusText: linkTokenResponse.statusText,
        body: errorText
      });
      throw new Error(`Failed to create Plaid link token: ${linkTokenResponse.status} ${linkTokenResponse.statusText}`);
    }
    
    const { link_token } = await linkTokenResponse.json();
    
    log.info('[connectPlaid] Link token created, opening Plaid Link');

    // Store link token so the /plaid-oauth callback page can resume the flow
    // if the user is redirected through a bank's OAuth page.
    sessionStorage.setItem('plaid_link_token_pending', link_token);
    
    // Step 2: Open Plaid Link in popup
    // Note: ToolExecutor handles timeout (5 minutes for OAuth flows)
    return new Promise((resolve, reject) => {
      // Import Plaid dynamically
      const script = document.createElement('script');
      script.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
      
      script.onerror = () => {
        log.error('[connectPlaid] Failed to load Plaid script');
        resolve(createErrorResult(
          'Failed to load Plaid',
          'Could not load Plaid Link script. Please check your internet connection.',
          'execution'
        ));
      };
      
      script.onload = () => {
        const plaid = (window as any).Plaid;
        
        if (!plaid) {
          log.error('[connectPlaid] Plaid not available after script load');
          resolve(createErrorResult(
            'Plaid not available',
            'Plaid Link script loaded but Plaid object not found.',
            'execution'
          ));
          return;
        }
        
        const linkHandler = plaid.create({
          token: link_token,
          onSuccess: async (public_token: string, metadata: any) => {
            log.info('[connectPlaid] User authorized, exchanging token');
            sessionStorage.removeItem('plaid_link_token_pending');
            
            try {
              // Step 3: Exchange public token
              const exchangeUrl = `${API_BASE_URL}/plaid/exchange_public_token`;
              log.info('[connectPlaid] Exchanging token:', { url: exchangeUrl });
              
              // Format accounts to match backend schema (Dict[str, str])
              // Plaid returns accounts with various fields, we need only string values
              const formattedAccounts = (metadata.accounts || []).map((acc: any) => {
                const formatted: Record<string, string> = {};
                
                // Only include string-convertible fields
                if (acc.id) formatted.id = String(acc.id);
                if (acc.name) formatted.name = String(acc.name);
                if (acc.type) formatted.type = String(acc.type);
                if (acc.subtype) formatted.subtype = String(acc.subtype);
                if (acc.mask) formatted.mask = String(acc.mask);
                
                // Add verification_status if present
                if (acc.verification_status) {
                  formatted.verification_status = String(acc.verification_status);
                }
                
                return formatted;
              });
              
              const requestBody = {
                public_token,
                institution: {
                  name: String(metadata.institution?.name || 'Unknown'),
                  institution_id: String(metadata.institution?.institution_id || '')
                },
                accounts: formattedAccounts
              };
              
              log.info('[connectPlaid] Exchange request:', { 
                publicTokenLength: public_token.length,
                institution: requestBody.institution,
                accountsCount: formattedAccounts.length,
                firstAccount: formattedAccounts[0]
              });
              
              const exchangeResponse = await fetch(exchangeUrl, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${authToken}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
              });
              
              if (!exchangeResponse.ok) {
                const errorText = await exchangeResponse.text();
                log.error('[connectPlaid] Exchange failed:', { 
                  status: exchangeResponse.status,
                  statusText: exchangeResponse.statusText,
                  body: errorText
                });
                
                let errorDetails = errorText;
                try {
                  const errorJson = JSON.parse(errorText);
                  errorDetails = errorJson.detail || errorJson.message || errorText;
                } catch {
                  // Keep as text
                }
                
                throw new Error(`Exchange failed (${exchangeResponse.status}): ${errorDetails}`);
              }
              
              const exchangeData = await exchangeResponse.json();
              const { success, item_id, sync_completed } = exchangeData;
              
              log.info('[connectPlaid] Connection successful:', { 
                success, 
                item_id, 
                sync_completed,
                institutionName: metadata.institution?.name 
              });
              
              // Step 4: Fix existing transactions that don't have org_id
              // (One-time migration for transactions synced before org_id support)
              try {
                const fixUrl = `${API_BASE_URL}/plaid/fix_org_ids`;
                log.info('[connectPlaid] Fixing org_ids for existing transactions:', { url: fixUrl });
                
                const fixResponse = await fetch(fixUrl, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                  }
                });
                
                if (fixResponse.ok) {
                  const fixData = await fixResponse.json();
                  log.info('[connectPlaid] Fixed org_ids:', fixData);
                } else {
                  log.warn('[connectPlaid] Could not fix org_ids (non-fatal)');
                }
              } catch (fixError) {
                log.warn('[connectPlaid] Failed to fix org_ids (non-fatal):', fixError);
              }
              
              // Step 5: Wait a moment for sync to complete, then check status
              if (autoSync && sync_completed) {
                log.info('[connectPlaid] Waiting for sync to complete...');
                await new Promise(resolve => setTimeout(resolve, 2000));
                
                // Check sync status (need database item ID, not Plaid item_id)
                // The backend should return the database _id, let's check what we got
                log.info('[connectPlaid] Checking sync status for item:', item_id);
                
                try {
                  const statusResponse = await fetch(
                    `${API_BASE_URL}/plaid/sync_status?item_id=${item_id}`,
                    {
                      headers: { 'Authorization': `Bearer ${authToken}` }
                    }
                  );
                  
                  if (statusResponse.ok) {
                    const status = await statusResponse.json();
                    
                    log.info('[connectPlaid] Sync status:', status);
                    
                    resolve(createSuccessResult(
                      `✅ Plaid connected successfully! Synced ${status.entity_count} transactions.`,
                      {
                        integration: 'plaid',
                        status: 'connected',
                        itemId: item_id,
                        syncCompleted: true,
                        transactionCount: status.entity_count,
                        dataAvailable: status.has_data,
                        dateRange: status.date_range,
                        message: 'Bank transactions are now available as BankTransaction entities. Use river.entity.query({ entityType: "BankTransaction" }) to access them.'
                      },
                      {
                        format: 'json',
                        metadata: {
                          affectedEntities: [item_id]
                        } as any
                      }
                    ));
                  } else {
                    // Sync status check failed, but connection succeeded
                    log.warn('[connectPlaid] Could not check sync status, but connection succeeded');
                    resolve(createSuccessResult(
                      '✅ Plaid connected successfully! Transactions are being synced.',
                      {
                        integration: 'plaid',
                        status: 'connected',
                        itemId: item_id,
                        syncCompleted: sync_completed,
                        message: 'Bank account connected. Transactions are being synced in the background.'
                      },
                      {
                        format: 'json',
                        metadata: {
                          affectedEntities: [item_id]
                        } as any
                      }
                    ));
                  }
                } catch (statusError) {
                  // Sync status failed, but connection succeeded
                  log.warn('[connectPlaid] Sync status check failed:', statusError);
                  resolve(createSuccessResult(
                    '✅ Plaid connected successfully!',
                    {
                      integration: 'plaid',
                      status: 'connected',
                      itemId: item_id,
                      message: 'Bank account connected. Transactions are being synced in the background.'
                    },
                    {
                      format: 'json',
                      metadata: {
                        affectedEntities: [item_id]
                      } as any
                    }
                  ));
                }
              } else {
                resolve(createSuccessResult(
                  '✅ Plaid connected successfully!',
                  {
                    integration: 'plaid',
                    status: 'connected',
                    itemId: item_id,
                    message: 'Bank account connected. Transactions are being synced in the background.'
                  },
                  {
                    format: 'json',
                    metadata: {
                      affectedEntities: [item_id]
                    } as any
                  }
                ));
              }
            } catch (error: any) {
              log.error('[connectPlaid] Exchange failed:', error);
              resolve(createErrorResult(
                'Failed to complete Plaid connection',
                error.message,
                'execution',
                {
                  error: {
                    code: 'EXCHANGE_FAILED',
                    category: 'execution',
                    details: error.message,
                    retryable: true
                  }
                }
              ));
            }
          },
          onExit: (err: any) => {
            sessionStorage.removeItem('plaid_link_token_pending');
            if (err) {
              log.error('[connectPlaid] User exited with error:', err);
              resolve(createErrorResult(
                'Plaid connection cancelled',
                err.display_message || err.error_message || 'User cancelled the connection',
                'execution',
                {
                  error: {
                    code: 'USER_CANCELLED',
                    category: 'execution',
                    details: err,
                    retryable: true
                  }
                }
              ));
            } else {
              log.info('[connectPlaid] User cancelled');
              resolve(createErrorResult(
                'Plaid connection cancelled',
                'User cancelled the connection',
                'execution',
                {
                  error: {
                    code: 'USER_CANCELLED',
                    category: 'execution',
                    details: 'User closed Plaid popup',
                    retryable: true
                  }
                }
              ));
            }
          }
        });
        
        try {
          linkHandler.open();
          log.info('[connectPlaid] Plaid Link opened successfully');
        } catch (openError: any) {
          log.error('[connectPlaid] Failed to open Plaid Link:', openError);
          resolve(createErrorResult(
            'Failed to open Plaid Link',
            openError.message || 'Could not open Plaid authorization popup',
            'execution'
          ));
        }
      };
      
      document.body.appendChild(script);
    });
    
  } catch (error: any) {
    log.error('[connectPlaid] Error:', error);
    return createErrorResult(
      'Failed to connect Plaid',
      error.message,
      'execution'
    );
  }
}

/**
 * Plaid update mode — re-authenticates an existing Item without a token exchange.
 * Called when an integration has status "reconnect_required".
 */
export async function reconnectPlaid(integrationId: string): Promise<void> {
  const authToken = localStorage.getItem('authToken');

  // Use the dedicated update-mode endpoint that reads the access_token server-side
  const updateTokenRes = await fetch(`${API_BASE_URL}/plaid/create_update_link_token`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ integration_id: integrationId, redirect_uri: `${window.location.origin}/plaid-oauth` }),
  });

  if (!updateTokenRes.ok) {
    const err = await updateTokenRes.text().catch(() => String(updateTokenRes.status));
    throw new Error(`Failed to create update link token: ${err}`);
  }

  const { link_token } = await updateTokenRes.json();

  sessionStorage.setItem('plaid_link_token_pending', link_token);
  sessionStorage.setItem('plaid_update_integration_id', integrationId);

  await new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector('script[src*="plaid.com/link"]');
    const load: Promise<void> = existingScript
      ? Promise.resolve()
      : new Promise((res, rej) => {
          const s = document.createElement('script');
          s.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
          s.onload = () => res();
          s.onerror = () => rej(new Error('Failed to load Plaid Link'));
          document.body.appendChild(s);
        });

    load.then(() => {
      const plaid = (window as any).Plaid;
      if (!plaid) return reject(new Error('Plaid SDK unavailable'));

      const handler = plaid.create({
        token: link_token,
        onSuccess: async (_public_token: string, _metadata: any) => {
          // No token exchange needed — access_token is unchanged after update mode
          sessionStorage.removeItem('plaid_link_token_pending');
          sessionStorage.removeItem('plaid_update_integration_id');
          try {
            await fetch(`${API_BASE_URL}/plaid/confirm_reconnect`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ integration_id: integrationId }),
            });
          } catch (e) {
            console.warn('[reconnectPlaid] confirm_reconnect failed (non-fatal):', e);
          }
          resolve();
        },
        onExit: (err: any) => {
          sessionStorage.removeItem('plaid_link_token_pending');
          sessionStorage.removeItem('plaid_update_integration_id');
          if (err) reject(new Error(err.display_message || err.error_message || 'Reconnect failed'));
          else resolve(); // user dismissed without error
        },
      });
      handler.open();
    }).catch(reject);
  });
}

/**
 * Gmail Connection (placeholder - to be implemented)
 */
async function connectGmail(context: CommandExecutionContext): Promise<StandardToolResult> {
  return createErrorResult(
    'Gmail connection not yet implemented',
    'Gmail integration is planned but not yet available. It will provide access to email data.',
    'validation'
  );
}

/**
 * Google Calendar Connection (placeholder)
 */
async function connectGoogleCalendar(context: CommandExecutionContext): Promise<StandardToolResult> {
  return createErrorResult(
    'Google Calendar connection not yet implemented',
    'Google Calendar integration is planned but not yet available.',
    'validation'
  );
}

/**
 * Google Drive Connection (placeholder)
 */
async function connectGoogleDrive(context: CommandExecutionContext): Promise<StandardToolResult> {
  return createErrorResult(
    'Google Drive connection not yet implemented',
    'Google Drive integration is planned but not yet available.',
    'validation'
  );
}

/**
 * Dropbox Connection (placeholder)
 */
async function connectDropbox(context: CommandExecutionContext): Promise<StandardToolResult> {
  return createErrorResult(
    'Dropbox connection not yet implemented',
    'Dropbox integration is planned but not yet available.',
    'validation'
  );
}

export const integrationCommands = [
  connectIntegrationCommand,
  setupIntegrationCommand
];
